import React, { useState, useEffect } from 'react';
import { logger } from '@/lib/logger';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Slider } from '@/components/ui/slider';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Settings, X, SlidersHorizontal, ImageIcon, CircuitBoard, RefreshCcw } from 'lucide-react';
import { useAppStore } from '@/stores/appStore';
import { useFloorPlanContext } from '../FloorPlanProvider';
import { useMarkerSizes } from '@/contexts/MarkerSizeContext';
import { toast } from 'sonner';
import { regenerateProjectLoadsAndKeypads } from '@/services/floorPlanService';
import { useQueryClient } from '@tanstack/react-query';
import { ScaleConfiguration } from './ScaleConfiguration';
import type { LegendConfig, MarkerSizesConfig } from '@/types/floorPlanViewer';
import { queryKeys } from '@/lib/queryKeys';

interface SettingsDrawerV2Props {
  onStartMeasurement?: (callback: (pixels: number) => void) => void;
  onStopMeasurement?: () => void;
  isMeasuring?: boolean;
  onScaleUpdate?: () => void;
}

export function SettingsDrawerV2({
  onStartMeasurement,
  onStopMeasurement,
  isMeasuring,
  onScaleUpdate,
}: SettingsDrawerV2Props = {}) {
  const activeDrawer = useAppStore(state => state.ui.activeDrawer);
  const setActiveDrawer = useAppStore(state => state.setActiveDrawer);
  const settingsTab = useAppStore(state => state.ui.settingsTab);
  const setSettingsTab = useAppStore(state => state.setSettingsTab);

  // View settings
  const showOnlyUnassigned = useAppStore(state => state.ui.toolbar.showOnlyUnassigned);
  const showOnlyUnassignedControls = useAppStore(
    state => state.ui.toolbar.showOnlyUnassignedControls,
  );
  const setFilter = useAppStore(state => state.setFilter);

  // Product settings
  const productIconStyle = useAppStore(state => state.products.iconStyle);
  const setProductIconStyle = useAppStore(state => state.setProductIconStyle);
  const isEditingFloorPlanImage = useAppStore(state => state.ui.isEditingFloorPlanImage);
  const setEditingFloorPlanImage = useAppStore(state => state.setEditingFloorPlanImage);
  const setActiveTool = useAppStore(state => state.setActiveTool);

  const {
    floorPlan,
    data,
    temporaryImageOpacity,
    setTemporaryImageOpacity,
    setTemporaryProductLabelFontSize,
  } = useFloorPlanContext();
  const queryClient = useQueryClient();
  const { sizes, setSize } = useMarkerSizes();

  // Use floor plan metadata from context (shared with other components like ControlLayer/LVProductLayer)
  const { floorPlanMetadata, saveFloorPlanMetadata } = data;

  // Local state
  const [isRegenerating, setIsRegenerating] = useState(false);

  const isOpen = activeDrawer === 'settings';

  const handleClose = () => {
    setActiveDrawer(null);
  };

  // Prevent drawer from closing when clicking inside
  const handleSheetInteraction = (e: Event) => {
    e.preventDefault();
  };

  // Handle grayscale toggle
  const handleGrayscaleChange = async (checked: boolean) => {
    try {
      await saveFloorPlanMetadata({ grayscale: checked });
    } catch (error) {
      logger.error('Error saving grayscale preference:', error);
      toast.error('Failed to save grayscale preference');
    }
  };

  // Handle legend visibility toggle
  const handleLegendVisibilityChange = async (checked: boolean) => {
    try {
      const currentMarkerSizes = (floorPlanMetadata?.marker_sizes as MarkerSizesConfig) || {};
      const currentLegendConfig = currentMarkerSizes.legend_config || {
        visible: true,
        x: 50,
        y: 50,
      };

      const newLegendConfig: LegendConfig = {
        ...currentLegendConfig,
        visible: checked,
      };

      await saveFloorPlanMetadata({
        marker_sizes: {
          ...currentMarkerSizes,
          legend_config: newLegendConfig,
        },
      });
    } catch (error) {
      logger.error('Error saving legend visibility:', error);
      toast.error('Failed to save legend visibility');
    }
  };

  // Local state for opacity slider (for UI display)
  const [localOpacity, setLocalOpacity] = useState<number>(floorPlanMetadata?.image_opacity ?? 100);

  // Sync local state when metadata changes externally (e.g., on initial load)
  // But only when temporaryImageOpacity is not set (i.e., not during/after a drag)
  useEffect(() => {
    // Only sync from metadata when there's no temporary override active
    if (temporaryImageOpacity === null) {
      const savedOpacity = floorPlanMetadata?.image_opacity ?? 100;
      setLocalOpacity(savedOpacity);
    }
  }, [floorPlanMetadata?.image_opacity, temporaryImageOpacity]);

  // Handle opacity change during drag (real-time visual feedback on floor plan)
  const handleOpacityDrag = (value: number[]) => {
    setLocalOpacity(value[0]);
    // Update the temporary opacity in context for real-time floor plan preview
    setTemporaryImageOpacity(value[0]);
  };

  // Handle opacity change when drag ends (save to database)
  // IMPORTANT: We keep temporaryImageOpacity set to avoid the "revert" issue caused by
  // startTransition() delaying the metadata state update. The temporary value will
  // persist until the next drag or until the component remounts (e.g., page refresh).
  const handleOpacityCommit = async (value: number[]) => {
    try {
      // Keep temporaryImageOpacity at the committed value (already set during drag)
      // This prevents the image from reverting while startTransition defers the state update
      await saveFloorPlanMetadata({ image_opacity: value[0] });
      // DON'T clear temporaryImageOpacity - it will stay set with the correct value
      // On page refresh, metadata will have the saved value and temporaryImageOpacity resets to null
    } catch (error) {
      logger.error('Error saving image opacity:', error);
      toast.error('Failed to save image opacity');
      // Revert to saved value on error
      setLocalOpacity(floorPlanMetadata?.image_opacity ?? 100);
      setTemporaryImageOpacity(null);
    }
  };

  // Get current legend visibility state
  const legendConfig = (floorPlanMetadata?.marker_sizes as MarkerSizesConfig)?.legend_config;
  const isLegendVisible = legendConfig?.visible ?? true; // Default to visible

  // Product label font size state
  const [localProductLabelFontSize, setLocalProductLabelFontSize] = useState<number>(
    (floorPlanMetadata?.marker_sizes as MarkerSizesConfig)?.product_label_font_size ?? 10,
  );

  // Sync product label font size when metadata changes
  useEffect(() => {
    const savedFontSize =
      (floorPlanMetadata?.marker_sizes as MarkerSizesConfig)?.product_label_font_size ?? 10;
    setLocalProductLabelFontSize(savedFontSize);
  }, [floorPlanMetadata?.marker_sizes]);

  // Handle product label font size change (during drag - real-time preview)
  const handleProductLabelFontSizeChange = (value: number[]) => {
    setLocalProductLabelFontSize(value[0]);
    // Update the temporary font size in context for real-time preview in ControlLayer/LVProductLayer
    setTemporaryProductLabelFontSize(value[0]);
  };

  // Handle product label font size commit (save to database)
  // IMPORTANT: We keep temporaryProductLabelFontSize set to avoid the "revert" issue caused by
  // startTransition() delaying the metadata state update. The temporary value will
  // persist until the next drag or until the component remounts (e.g., page refresh).
  const handleProductLabelFontSizeCommit = async (value: number[]) => {
    try {
      const currentMarkerSizes = (floorPlanMetadata?.marker_sizes as MarkerSizesConfig) || {};
      await saveFloorPlanMetadata({
        marker_sizes: {
          ...currentMarkerSizes,
          product_label_font_size: value[0],
        },
      });
      // DON'T clear temporaryProductLabelFontSize - it will stay set with the correct value
      // On page refresh, metadata will have the saved value and temporaryProductLabelFontSize resets to null
    } catch (error) {
      logger.error('Error saving product label font size:', error);
      toast.error('Failed to save product label font size');
      // Revert to saved value on error
      const savedFontSize =
        (floorPlanMetadata?.marker_sizes as MarkerSizesConfig)?.product_label_font_size ?? 10;
      setLocalProductLabelFontSize(savedFontSize);
      setTemporaryProductLabelFontSize(null);
    }
  };

  // Handle regenerate project loads and centralized keypads
  const handleRegenerateLoadsAndKeypads = async () => {
    try {
      setIsRegenerating(true);
      toast.info('Starting project-wide load and keypad regeneration...');
      const { success, message, error } = await regenerateProjectLoadsAndKeypads(floorPlan.id);

      if (success) {
        toast.success(message || 'Project loads and keypads regenerated successfully');
        // Refresh the data
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: queryKeys.entities('circuits') }),
          queryClient.invalidateQueries({ queryKey: queryKeys.entities('lighting_controls') }),
          queryClient.invalidateQueries({ queryKey: queryKeys.entities('lighting_fixtures') }),
        ]);
      } else {
        throw error || new Error(message || 'Failed to regenerate project loads and keypads');
      }
    } catch (error) {
      logger.error('Error regenerating project loads and keypads:', error);
      toast.error('Failed to regenerate project loads and keypads');
    } finally {
      setIsRegenerating(false);
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={open => !open && handleClose()} modal={false}>
      <SheetContent
        side="right"
        className="w-full sm:w-[400px] overflow-y-auto flex flex-col h-full [&>button]:hidden"
        onPointerDownOutside={handleSheetInteraction}
        onInteractOutside={handleSheetInteraction}
      >
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Floor Plan Options
          </SheetTitle>
          <Button
            variant="ghost"
            size="icon"
            className="absolute right-4 top-4"
            onClick={handleClose}
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </Button>
        </SheetHeader>

        <Tabs
          value={settingsTab || 'general'}
          onValueChange={setSettingsTab}
          className="flex-1 overflow-hidden flex flex-col mt-6"
        >
          <TabsList className="grid grid-cols-5 mb-4">
            <TabsTrigger value="general" className="flex items-center gap-1 text-xs">
              <SlidersHorizontal className="h-3 w-3" />
              <span className="hidden sm:inline">General</span>
            </TabsTrigger>
            <TabsTrigger value="scale" className="flex items-center gap-1 text-xs">
              <span>Scale</span>
            </TabsTrigger>
            <TabsTrigger value="image" className="flex items-center gap-1 text-xs">
              <ImageIcon className="h-3 w-3" />
              <span className="hidden sm:inline">Image</span>
            </TabsTrigger>
            <TabsTrigger value="circuits" className="flex items-center gap-1 text-xs">
              <CircuitBoard className="h-3 w-3" />
              <span className="hidden sm:inline">Loads</span>
            </TabsTrigger>
            <TabsTrigger value="style" className="flex items-center gap-1 text-xs">
              <span>Style</span>
            </TabsTrigger>
          </TabsList>

          <div className="flex-1 overflow-y-auto">
            <TabsContent value="general" className="p-4 space-y-4">
              {/* Filter Options */}
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <SlidersHorizontal className="h-4 w-4 text-muted-foreground" />
                  <h3 className="text-sm font-medium">Filter Options</h3>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 border rounded-lg">
                    <Label htmlFor="show-unassigned" className="cursor-pointer text-sm font-normal">
                      Show only unassigned fixtures
                    </Label>
                    <Switch
                      id="show-unassigned"
                      checked={showOnlyUnassigned}
                      onCheckedChange={checked => setFilter('showOnlyUnassigned', checked)}
                    />
                  </div>

                  <div className="flex items-center justify-between p-3 border rounded-lg">
                    <Label
                      htmlFor="show-unassigned-controls"
                      className="cursor-pointer text-sm font-normal"
                    >
                      Show only unassigned controls
                    </Label>
                    <Switch
                      id="show-unassigned-controls"
                      checked={showOnlyUnassignedControls}
                      onCheckedChange={checked => setFilter('showOnlyUnassignedControls', checked)}
                    />
                  </div>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="scale" className="p-4">
              <ScaleConfiguration
                floorPlanId={floorPlan.id}
                onStartMeasurement={onStartMeasurement}
                onStopMeasurement={onStopMeasurement}
                isMeasuring={isMeasuring}
                onScaleUpdate={onScaleUpdate}
              />
            </TabsContent>

            <TabsContent value="image" className="p-4 space-y-4">
              {/* Floor Plan Settings */}
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <ImageIcon className="h-4 w-4 text-muted-foreground" />
                  <h3 className="text-sm font-medium">Floor Plan Settings</h3>
                </div>

                <div className="space-y-3">
                  <div className="border rounded-lg p-3">
                    <div className="flex items-center justify-between">
                      <div className="space-y-1">
                        <Label
                          htmlFor="edit-floor-plan"
                          className="cursor-pointer text-sm font-normal"
                        >
                          Edit Floor Plan Image
                        </Label>
                        <p className="text-xs text-muted-foreground">
                          Resize and reposition the floor plan image
                        </p>
                      </div>
                      <Switch
                        id="edit-floor-plan"
                        checked={isEditingFloorPlanImage}
                        onCheckedChange={checked => {
                          setEditingFloorPlanImage(checked);
                          if (checked) {
                            // Get current states from store
                            const state = useAppStore.getState();

                            // Reset all active modes and tools
                            setActiveTool('select');
                            state.setActiveMode('default');

                            // Reset any annotation mode
                            state.setActiveAnnotationType(null);

                            // Auto-close drawer when entering edit mode
                            setActiveDrawer(null);
                            toast.info(
                              'Click and drag to move, use resize handles to resize the floor plan',
                            );
                          }
                        }}
                      />
                    </div>
                  </div>

                  <div className="border rounded-lg p-3">
                    <div className="flex items-center justify-between">
                      <div className="space-y-1">
                        <Label
                          htmlFor="grayscale-floor-plan"
                          className="cursor-pointer text-sm font-normal"
                        >
                          Grayscale Floor Plan
                        </Label>
                        <p className="text-xs text-muted-foreground">
                          Convert the floor plan image to grayscale
                        </p>
                      </div>
                      <Switch
                        id="grayscale-floor-plan"
                        checked={floorPlanMetadata?.grayscale || false}
                        onCheckedChange={handleGrayscaleChange}
                      />
                    </div>
                  </div>

                  <div className="border rounded-lg p-3">
                    <div className="space-y-3">
                      <div className="space-y-1">
                        <div className="flex items-center justify-between">
                          <Label
                            htmlFor="image-opacity"
                            className="cursor-pointer text-sm font-normal"
                          >
                            Image Opacity
                          </Label>
                          <span className="text-xs text-muted-foreground font-medium">
                            {localOpacity}%
                          </span>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Reduce opacity to make icons stand out more
                        </p>
                      </div>
                      <Slider
                        id="image-opacity"
                        min={10}
                        max={100}
                        step={5}
                        value={[localOpacity]}
                        onValueChange={handleOpacityDrag}
                        onValueCommit={handleOpacityCommit}
                        className="w-full"
                      />
                    </div>
                  </div>

                  <div className="border rounded-lg p-3">
                    <div className="flex items-center justify-between">
                      <div className="space-y-1">
                        <Label htmlFor="show-legend" className="cursor-pointer text-sm font-normal">
                          Show Symbol Legend
                        </Label>
                        <p className="text-xs text-muted-foreground">
                          Display a legend of all symbols used on the floor plan
                        </p>
                      </div>
                      <Switch
                        id="show-legend"
                        checked={isLegendVisible}
                        onCheckedChange={handleLegendVisibilityChange}
                      />
                    </div>
                  </div>
                </div>

                {!floorPlanMetadata && (
                  <div className="bg-muted/50 border rounded-lg p-4 text-center">
                    <p className="text-sm text-muted-foreground">
                      No floor plan metadata available. Upload a floor plan to get started.
                    </p>
                  </div>
                )}
              </div>
            </TabsContent>

            <TabsContent value="circuits" className="p-4 space-y-4">
              {/* Load Management */}
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <CircuitBoard className="h-4 w-4 text-muted-foreground" />
                  <h3 className="text-sm font-medium">Load Management</h3>
                </div>

                <div className="bg-muted/50 border rounded-lg p-4">
                  <div className="space-y-3">
                    <div className="flex items-start gap-2">
                      <div className="mt-1">
                        <div className="h-2 w-2 rounded-full bg-muted-foreground/50" />
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Regenerate load and centralized keypad numbers project-wide to make them
                        sequential without gaps
                      </p>
                    </div>
                    <div className="flex items-start gap-2">
                      <div className="mt-1">
                        <div className="h-2 w-2 rounded-full bg-muted-foreground/50" />
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Ensures unique sequential numbering across all floor plans in the project
                      </p>
                    </div>
                  </div>
                </div>

                <Button
                  variant="outline"
                  size="sm"
                  className="w-full flex items-center justify-center gap-2"
                  onClick={handleRegenerateLoadsAndKeypads}
                  disabled={isRegenerating}
                >
                  {isRegenerating ? (
                    <>
                      <RefreshCcw className="h-4 w-4 animate-spin" />
                      Updating Project Loads & Keypads...
                    </>
                  ) : (
                    <>
                      <RefreshCcw className="h-4 w-4" />
                      Regenerate Project Loads & Keypads
                    </>
                  )}
                </Button>
              </div>
            </TabsContent>

            <TabsContent value="style" className="p-4 space-y-4">
              {/* Icon Style */}
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <SlidersHorizontal className="h-4 w-4 text-muted-foreground" />
                  <h3 className="text-sm font-medium">Icon Style</h3>
                </div>

                <div className="border rounded-lg p-3">
                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <Label htmlFor="high-contrast" className="cursor-pointer text-sm font-normal">
                        High Contrast Icons
                      </Label>
                      <p className="text-xs text-muted-foreground">
                        Use colored backgrounds for better visibility
                      </p>
                    </div>
                    <Switch
                      id="high-contrast"
                      checked={productIconStyle === 'high-contrast'}
                      onCheckedChange={checked => {
                        setProductIconStyle(checked ? 'high-contrast' : 'minimalist');
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* Label Settings */}
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <SlidersHorizontal className="h-4 w-4 text-muted-foreground" />
                  <h3 className="text-sm font-medium">Label Settings</h3>
                </div>

                <div className="border rounded-lg p-3">
                  <div className="space-y-3">
                    <div className="space-y-1">
                      <div className="flex items-center justify-between">
                        <Label
                          htmlFor="product-label-font-size"
                          className="cursor-pointer text-sm font-normal"
                        >
                          Product Label Size
                        </Label>
                        <span className="text-xs text-muted-foreground font-medium">
                          {localProductLabelFontSize}px
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Font size for control and LV product labels
                      </p>
                    </div>
                    <Slider
                      id="product-label-font-size"
                      min={8}
                      max={24}
                      step={1}
                      value={[localProductLabelFontSize]}
                      onValueChange={handleProductLabelFontSizeChange}
                      onValueCommit={handleProductLabelFontSizeCommit}
                      className="w-full"
                    />
                  </div>
                </div>
              </div>
            </TabsContent>
          </div>
        </Tabs>
      </SheetContent>
    </Sheet>
  );
}
